/**
 * Launcher V2 PR C U5 — per-channel readiness derivation (R3, R6): the pure
 * "is this rung actually ready to send?" logic behind the Ready/Not-ready
 * badge on the Qualify rung rows, the Sequences rail cards, and the Review
 * rows.
 *
 * DOM-free on purpose, same reason as `review-gate.ts` / `rung-counts.ts` /
 * `playbook-draft.ts`: `bun:test` has no DOM under `mini-apps/`, so any logic
 * that lives inside a Glaze-importing `.tsx` file is untestable there.
 *
 * ── PORTED, NOT INVENTED ──────────────────────────────────────────────────
 * The per-channel rule table below is v1's, read verbatim off
 * `mini-apps/launcher/components/WaterfallConfig.tsx:652-668` (the "Ready"
 * flip-button's validation):
 *
 *   - Sender required on every channel. Playbooks QA round 4, U9 (SN1) /
 *     Decision 3 — the value checked is still `mapping.sender` and this
 *     module is UNCHANGED by that unit; what changed is where that value
 *     comes from and where a rep fixes it. Sender is now locked to the
 *     SEQUENCE identity (`playbooks_sequence.default_sender`) and COPIED onto
 *     `mapping.sender` when a rung maps the sequence, so the only place to
 *     resolve a `'no sender'` badge is Config → Sequences (the rung-level
 *     popover is gone). Existing mapped rungs keep their copied sender, so
 *     nothing that was ready became unready; a NEW mapping of a sequence
 *     whose `default_sender` is still null copies `''` and flags here
 *     exactly as before (Decision 10 ships no backfill).
 *   - Flow id required on every channel (v2 tightens this: v1 only required
 *     it for `gong_engage`'s Gong flow id column, but phases-doc Decision 5
 *     makes `playbooks_sequence.flow_id` a REQUIRED lock on every channel in
 *     v2 — there is no channel where a sequence can go out with no bound
 *     flow).
 *   - Custom copy required ONLY for `slack_copilot` and `inflection` — v1's
 *     `cell.has_custom_copy` gate. `gong_engage` copy comes from the Gong
 *     step templates themselves (`gongStepDescriptor`), not from a locally
 *     authored body, so it is not required here.
 *
 * v1's merge-tag check (body must contain `{{First Name}}` /
 * `{{Sender First Name}}`) is DELIBERATELY NOT ported: the task instructions
 * for this unit scope "step copy" to `copyStatus`/non-empty body, and
 * per-token gap checking is U6's `copy-token-flags.ts` (a sibling unit,
 * built against real member data) — folding token-gap validation in here
 * would duplicate that module's job with a second, unsynchronized rule.
 *
 * ── COPYSTATUS FLIP UX (the plan's deferred question) ────────────────────
 * v1's "Ready" was a MANUAL flip button that only committed `copy_status:
 * 'ready'` once its checks passed. V2 does not port the manual flip: nothing
 * in this unit's scope adds a control that writes `SequenceStep.copyStatus`,
 * and `PlaybooksSaveSequenceStepsRequestSchema` already accepts any
 * `copyStatus` value (or none) unconditionally — the backend has no
 * enforcement here (v1 parity; enforcement-at-send is PR D). So v2's
 * readiness is a PURE, always-on DERIVATION rather than a manually-flipped
 * flag: a step counts as "copy ready" when its own `copyStatus` already
 * reads `'ready'` OR (for a step that has never been explicitly flipped) it
 * simply has a non-empty `body` — see {@link hasReadyCopy}. This keeps the
 * badge honest without inventing a second UI surface this unit doesn't own.
 */

import { CHANNELS } from '../_shared/launcher-copy-tokens';
import type { LauncherChannel, SequenceCopyStatus, SequenceMapping } from './schemas';
import { effectiveSender } from './sender-choice';
import { firstStepMissingSubject } from './step-content-rules';

// Per-channel required-field table

/**
 * The things readiness can require — see the module doc for the source rule.
 * `'subject'` (QA round 7, U4, item 6) — step 1 needs a subject line on the
 * email channels (`gong_engage`, `inflection`); see
 * `step-content-rules.ts#firstStepMissingSubject` for the reply-ordinal
 * exception this reason defers to.
 */
export type ReadinessRequirement = 'sender' | 'flowId' | 'copy' | 'subject';

/**
 * One row per channel, in the closed `LauncherChannelSchema` vocabulary.
 * Exported so a caller (or a future channel-config surface) can render the
 * requirement list without re-deriving it from `rungReadiness`'s internals.
 */
export const CHANNEL_READINESS_REQUIREMENTS: Record<
  LauncherChannel,
  readonly ReadinessRequirement[]
> = {
  gong_engage: ['sender', 'flowId', 'subject'],
  inflection: ['sender', 'flowId', 'copy', 'subject'],
  slack_copilot: ['sender', 'flowId', 'copy'],
};

// Inputs

/**
 * The minimum a rung has to expose for readiness purposes — `PlaybookRung`
 * and `playbook-draft.ts#DraftRung` both satisfy it structurally. Only
 * `mapping` matters: readiness of an unmapped rung is always `'unmapped'`
 * (see {@link rungReadiness}).
 */
export interface ReadinessRung {
  mapping: Pick<SequenceMapping, 'sender'> | null;
}

/**
 * One step's copy, as far as readiness cares — see {@link hasReadyCopy}.
 * `position`/`subject` (QA round 7, U4, item 6) feed
 * {@link firstStepMissingSubject}; `position` is required (not optional) so
 * that check can find the true first step rather than trusting array order.
 */
export interface ReadinessStep {
  position: number;
  subject?: string;
  copyStatus?: SequenceCopyStatus;
  body?: string;
}

/**
 * The sequence-side facts readiness needs, alongside the rung's own mapping.
 * Deliberately NOT `PlaybooksSequenceSummary` (which carries no step
 * content) — a caller assembles this from whatever it has loaded: the
 * library summary's `channel`/`flowId` are always cheap (already fetched),
 * `steps` requires the sequence's head version.
 *
 * `steps` is OPTIONAL and treated as "not yet known", not "no copy" — a
 * caller that has not loaded the sequence's steps yet (e.g. a rung row that
 * has not been opened in the Sequences step this session) gets `'not_ready'`
 * for a copy-requiring channel rather than a false `'ready'`. Readiness never
 * claims more than it can currently verify.
 *
 * `sender` — Playbooks QA round 5, R5-4 — the sequence's OWN
 * `playbooks_sequence.default_sender`, required (not optional) for the same
 * reason `flowId` is: once a caller has loaded the sequence's facts at all,
 * it has this too (`PlaybooksSequenceSummary.sender` is never behind a
 * separate fetch), and an accidentally-omitted field should be a compile
 * error, not a silent `undefined` read. See {@link effectiveSender} in
 * `sender-choice.ts` for how this and the rung's stale `mapping.sender` copy
 * are reconciled — the sequence's value wins outright once this input
 * exists at all; there is no merge.
 */
export interface SequenceReadinessInput {
  channel: LauncherChannel;
  flowId: string | null;
  sender: string | null;
  steps?: readonly ReadinessStep[];
  /**
   * QA round 7, U4 (item 6) — the bound Gong flow's live reply-step
   * ordinals, from `useGongReplyStepOrders`. `undefined` = "unknown" (the
   * flow's shape hasn't loaded yet, or there is no bound flow) — see
   * {@link rungReadinessReasons}'s doc for why `gong_engage` skips the
   * `subject` reason entirely rather than guess while this is `undefined`.
   * Unused for `inflection`/`slack_copilot`, which have no reply-step
   * concept.
   */
  replyStepOrders?: ReadonlySet<number>;
}

export type RungReadiness = 'ready' | 'not_ready' | 'unmapped';

/**
 * `true` when every step's copy counts as ready: either the step has been
 * explicitly marked `copyStatus: 'ready'`, or (for a step nothing has ever
 * flipped) it simply has non-empty body text — see the module doc's
 * "copyStatus flip UX" note for why there is no separate manual-flip gate.
 * An empty `steps` array is never ready — there is no copy at all to send.
 */
function hasReadyCopy(steps: readonly ReadinessStep[]): boolean {
  if (steps.length === 0) return false;
  return steps.every((step) => step.copyStatus === 'ready' || (step.body?.trim().length ?? 0) > 0);
}

/**
 * Round-3c QA fixes, H3 — the unmet requirements behind a `'not_ready'`
 * verdict, in the same order {@link CHANNEL_READINESS_REQUIREMENTS} lists
 * them (`'sender'` → `'flowId'` → `'copy'`). Spencer's QA: he edited copy on
 * a Gong rung with an EMPTY sender and could not tell why the badge stayed
 * "Not ready" — `gong_engage` doesn't require copy, so the one thing
 * actually missing (the sender) was invisible. `[]` for an unmapped rung
 * (there is nothing to be "not ready" ABOUT) and for a rung that is
 * genuinely `'ready'`.
 *
 * `sender` is checked unconditionally rather than gated on
 * `CHANNEL_READINESS_REQUIREMENTS`: every channel in the current table
 * requires it. Playbooks QA round 5, R5-4 — what counts as "the sender" is
 * now {@link effectiveSender}'s (`sender-choice.ts`) live read, not
 * `rung.mapping.sender` directly: once `sequence` has loaded, its OWN
 * `sender` field is authoritative (a Config edit reaches every rung
 * immediately), and `rung.mapping.sender` is consulted only as the
 * loading-gap fallback while `sequence` is still `undefined`.
 *
 * `flowId`/`copy` still need `sequence`'s other facts to evaluate, so those
 * two are unaffected by this change.
 *
 * `flowId` is reported missing both when the sequence's facts have not
 * loaded yet (`sequence === undefined`) and when they have loaded with no
 * `flowId` set — the same conservative "unknown counts as missing, never a
 * guessed pass" rule {@link rungReadiness} always applied here, just now
 * surfaced as a specific reason instead of a bare `'not_ready'`. `copy` can
 * only be evaluated once `sequence.channel` is known, so it's skipped
 * entirely (not reported as missing OR satisfied) while `sequence` is
 * `undefined`.
 *
 * `subject` (QA round 7, U4, item 6) is skipped — never reported missing OR
 * satisfied — under three conditions, each a form of "not yet knowable":
 * `sequence === undefined` (nothing loaded), `sequence.steps === undefined`
 * (the sequence's head hasn't loaded), and `gong_engage` specifically while
 * `sequence.replyStepOrders === undefined` (the bound flow's reply shape
 * hasn't loaded — an unknown shape must never manufacture a false "Not
 * ready", since the flow might turn out to reply at ordinal 1). The last
 * case is `gong_engage`-only because `inflection` has no reply-step concept
 * at all — its subject check is never gated on `replyStepOrders`.
 */
export function rungReadinessReasons(
  rung: ReadinessRung,
  sequence: SequenceReadinessInput | undefined,
): ReadinessRequirement[] {
  if (rung.mapping === null) return [];

  const reasons: ReadinessRequirement[] = [];

  if (effectiveSender(sequence, rung.mapping).trim().length === 0) reasons.push('sender');
  if (sequence === undefined || !sequence.flowId) reasons.push('flowId');

  if (sequence !== undefined) {
    const required = CHANNEL_READINESS_REQUIREMENTS[sequence.channel];
    if (required.includes('copy') && (sequence.steps === undefined || !hasReadyCopy(sequence.steps))) {
      reasons.push('copy');
    }
    if (
      required.includes('subject') &&
      sequence.steps !== undefined &&
      !(sequence.channel === 'gong_engage' && sequence.replyStepOrders === undefined) &&
      firstStepMissingSubject(sequence.steps, sequence.replyStepOrders ?? null)
    ) {
      reasons.push('subject');
    }
  }

  return reasons;
}

/**
 * The Ready/Not-ready/unmapped classification a rung's row/card renders.
 *
 * - `'unmapped'` — `rung.mapping === null`. This is `review-gate.ts`'s
 *   existing "No sequence" bucket; nothing about it changes here, and the
 *   badge renders NOTHING for this case (there is no "not ready" badge for a
 *   rung that was never supposed to send — see this unit's test scenarios).
 * - `'not_ready'` — mapped, and {@link rungReadinessReasons} reports at
 *   least one unmet requirement (a missing required field, or the
 *   sequence's facts not loaded yet).
 * - `'ready'` — mapped, and every required field for the channel is present
 *   (`rungReadinessReasons` reports none).
 *
 * Derived from {@link rungReadinessReasons} (H3) rather than re-deriving the
 * same per-channel logic independently, so the two can never disagree about
 * the same inputs.
 */
export function rungReadiness(
  rung: ReadinessRung,
  sequence: SequenceReadinessInput | undefined,
): RungReadiness {
  if (rung.mapping === null) return 'unmapped';
  return rungReadinessReasons(rung, sequence).length === 0 ? 'ready' : 'not_ready';
}

/** Plain-English words for the requirements whose wording never varies by channel — never "mapping". */
const READINESS_REQUIREMENT_LABELS: Record<Exclude<ReadinessRequirement, 'flowId'>, string> = {
  sender: 'no sender',
  copy: 'copy not finished',
  subject: 'step 1 subject',
};

/**
 * Each channel's display label, derived from `CHANNELS` (`_shared/launcher-copy-tokens.ts`) — the same
 * array both create-forms render — never re-spelled here. Built as an explicit `Record<LauncherChannel,
 * string>` (not `Object.fromEntries`) so a future 4th channel added to `LauncherChannelSchema` WITHOUT a
 * matching entry added here is a compile error, not a silent `undefined` at runtime.
 *
 * What this shape does NOT guarantee (round-3 QA fix, F8): it protects the union → this-map direction
 * only. It does nothing for the reverse — a channel listed here (and in `LauncherChannelSchema`) that gets
 * removed from `CHANNELS` itself. `.find(...)!` below would then return `undefined`, and the `!` assertion
 * turns that into a `TypeError` thrown at MODULE LOAD, not at any call site. Nothing in the type system
 * catches that; the `LauncherChannelSchema` vs. `CHANNELS` exhaustiveness test in
 * `__tests__/sequence-readiness.test.ts` is what pins it instead.
 */
const CHANNEL_LABEL_BY_VALUE: Record<LauncherChannel, string> = {
  gong_engage: CHANNELS.find((c) => c.value === 'gong_engage')!.label,
  inflection: CHANNELS.find((c) => c.value === 'inflection')!.label,
  slack_copilot: CHANNELS.find((c) => c.value === 'slack_copilot')!.label,
};

/**
 * playbooks-inflection-wiring (2026-09-17) — the exported form of
 * `CHANNEL_LABEL_BY_VALUE`, so `SequenceFlowIdPopover` (and any other
 * non-readiness caller) reads the SAME channel-label vocabulary this module
 * already uses for badge copy, rather than a third ad-hoc copy
 * (`PlaybooksConfigPage.tsx:133`, `SequencesStep.tsx:304` each had their own
 * before this).
 */
export function channelLabel(channel: LauncherChannel): string {
  return CHANNEL_LABEL_BY_VALUE[channel];
}

/**
 * The `flowId` reason's wording, channel-aware (round-3 QA fixes, U4): "no Gong Engage flow" / "no
 * Inflection flow" / "no Terra Co-Pilot flow". `channel === null` (no sequence to read a channel from)
 * renders the neutral "no flow" rather than guessing — see the module's "No Hardcoded Fallbacks" rule.
 */
function flowIdLabel(channel: LauncherChannel | null): string {
  return channel === null ? 'no flow' : `no ${CHANNEL_LABEL_BY_VALUE[channel]} flow`;
}

/**
 * Round-3c QA fixes, H3 — the badge copy for a `'not_ready'` rung: "Not
 * ready — no sender" / "Not ready — no Gong Engage flow" / "Not ready — copy
 * not finished", several reasons joined with `", "` (e.g. "Not ready — no
 * sender, no Inflection flow"). The three call sites (`SequencesStep`'s rail
 * card, `QualifyStep`'s `RungRow`, `ReviewStep`'s `ReviewRow`) all render
 * this exact string rather than each inventing their own join, so the
 * wording can't drift between them.
 *
 * Round-3 QA fixes, U4 — `flowId`'s wording now names the missing channel's
 * own flow ("no Gong Engage flow" / "no Inflection flow" / "no Terra
 * Co-Pilot flow") instead of always "no Gong flow"; `channel` is the mapped
 * rung's sequence's channel, or `null` when no sequence is available yet
 * (renders the neutral "no flow"). `sender`/`copy` wording is unaffected by
 * `channel`.
 *
 * Callers only render this for `readiness === 'not_ready'`, where `reasons`
 * is guaranteed non-empty by {@link rungReadiness}'s own derivation.
 */
export function notReadyBadgeText(
  reasons: readonly ReadinessRequirement[],
  channel: LauncherChannel | null,
): string {
  const label = (reason: ReadinessRequirement): string =>
    reason === 'flowId' ? flowIdLabel(channel) : READINESS_REQUIREMENT_LABELS[reason];
  return `Not ready — ${reasons.map(label).join(', ')}`;
}
